/**
 * PitchBook Admin API
 * PitchBook Admin OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {ApiLimit} from '../models/ApiLimit';
import type {ChangesInfo} from '../models/ChangesInfo';
import type {Error} from '../models/Error';
import type {LimitList} from '../models/LimitList';
import type {PricingModel} from '../models/PricingModel';

export interface GetContractLimits$Request {
    accountId: number;
    contractId: number;
    pricingModel: PricingModel;
}

export interface GetContractLimits$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getContractLimitsRequest = createAction<PayloadData<void, GetContractLimits$Request>>('LIMIT_CONTROLLER__GET_CONTRACT_LIMITS__REQUEST');
export const getContractLimitsSuccess = createAction<PayloadData<LimitList, GetContractLimits$Request>>('LIMIT_CONTROLLER__GET_CONTRACT_LIMITS__SUCCESS');
export const getContractLimitsFailure = createAction<PayloadData<Error, GetContractLimits$Request, true>>('LIMIT_CONTROLLER__GET_CONTRACT_LIMITS__FAILURE');

export const getContractLimitsActions = [
    getContractLimitsRequest,
    getContractLimitsSuccess,
    getContractLimitsFailure
] as const;

/**
 * Get contract-level call limits
 * @param {number} meta.accountId Id of account
 * @param {number} meta.contractId Id of contract
 * @param {PricingModel} meta.pricingModel Contract Pricing Model
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getContractLimits = (meta: GetContractLimits$Request, config?: GetContractLimits$Config) => {
    const TRANSPORT_GET_PARAMS = {
        pricingModel: meta.pricingModel,
    };
    return {
        CALL_API: {
            types: getContractLimitsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/${meta.contractId}/call-limits`,
            TRANSPORT_GET_PARAMS,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetLimits$Request {
    accountId: number;
    pricingModel: PricingModel;
}

export interface GetLimits$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getLimitsRequest = createAction<PayloadData<void, GetLimits$Request>>('LIMIT_CONTROLLER__GET_LIMITS__REQUEST');
export const getLimitsSuccess = createAction<PayloadData<LimitList, GetLimits$Request>>('LIMIT_CONTROLLER__GET_LIMITS__SUCCESS');
export const getLimitsFailure = createAction<PayloadData<Error, GetLimits$Request, true>>('LIMIT_CONTROLLER__GET_LIMITS__FAILURE');

export const getLimitsActions = [
    getLimitsRequest,
    getLimitsSuccess,
    getLimitsFailure
] as const;

/**
 * Get call limits
 * @param {number} meta.accountId Id of account
 * @param {PricingModel} meta.pricingModel Contract Pricing Model
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getLimits = (meta: GetLimits$Request, config?: GetLimits$Config) => {
    const TRANSPORT_GET_PARAMS = {
        pricingModel: meta.pricingModel,
    };
    return {
        CALL_API: {
            types: getLimitsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/call-limits`,
            TRANSPORT_GET_PARAMS,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface SaveContractLimits$Request {
    accountId: number;
    contractId: number;
    pricingModel: PricingModel;
    apiLimit?: Array<ApiLimit>;
}

export interface SaveContractLimits$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const saveContractLimitsRequest = createAction<PayloadData<void, SaveContractLimits$Request>>('LIMIT_CONTROLLER__SAVE_CONTRACT_LIMITS__REQUEST');
export const saveContractLimitsSuccess = createAction<PayloadData<ChangesInfo, SaveContractLimits$Request>>('LIMIT_CONTROLLER__SAVE_CONTRACT_LIMITS__SUCCESS');
export const saveContractLimitsFailure = createAction<PayloadData<Error, SaveContractLimits$Request, true>>('LIMIT_CONTROLLER__SAVE_CONTRACT_LIMITS__FAILURE');

export const saveContractLimitsActions = [
    saveContractLimitsRequest,
    saveContractLimitsSuccess,
    saveContractLimitsFailure
] as const;

/**
 * Save contract-level call limits
 * @param {number} meta.accountId Id of account
 * @param {number} meta.contractId Id of contract
 * @param {PricingModel} meta.pricingModel Contract Pricing Model
 * @param {Array<ApiLimit>} [meta.apiLimit]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const saveContractLimits = (meta: SaveContractLimits$Request, config?: SaveContractLimits$Config) => {
    const TRANSPORT_GET_PARAMS = {
        pricingModel: meta.pricingModel,
    };
    return {
        CALL_API: {
            types: saveContractLimitsActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/${meta.contractId}/call-limits`,
            TRANSPORT_GET_PARAMS,
            TRANSPORT_REQUEST_BODY: meta.apiLimit,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface SaveLimits$Request {
    accountId: number;
    pricingModel: PricingModel;
    apiLimit?: Array<ApiLimit>;
}

export interface SaveLimits$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const saveLimitsRequest = createAction<PayloadData<void, SaveLimits$Request>>('LIMIT_CONTROLLER__SAVE_LIMITS__REQUEST');
export const saveLimitsSuccess = createAction<PayloadData<ChangesInfo, SaveLimits$Request>>('LIMIT_CONTROLLER__SAVE_LIMITS__SUCCESS');
export const saveLimitsFailure = createAction<PayloadData<Error, SaveLimits$Request, true>>('LIMIT_CONTROLLER__SAVE_LIMITS__FAILURE');

export const saveLimitsActions = [
    saveLimitsRequest,
    saveLimitsSuccess,
    saveLimitsFailure
] as const;

/**
 * Get call limits
 * @param {number} meta.accountId Id of account
 * @param {PricingModel} meta.pricingModel Contract Pricing Model
 * @param {Array<ApiLimit>} [meta.apiLimit]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const saveLimits = (meta: SaveLimits$Request, config?: SaveLimits$Config) => {
    const TRANSPORT_GET_PARAMS = {
        pricingModel: meta.pricingModel,
    };
    return {
        CALL_API: {
            types: saveLimitsActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/api-v2/${meta.accountId}/call-limits`,
            TRANSPORT_GET_PARAMS,
            TRANSPORT_REQUEST_BODY: meta.apiLimit,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

